import React, { useEffect } from 'react';
import MemoryGame from './MemoryGame';
import PatternRecognition from './PatternRecognition';

// Puzzle type constants
export const PUZZLE_TYPES = {
  MEMORY_GAME: 'memory_game',
  PATTERN_RECOGNITION: 'pattern_recognition',
};

// Default configurations for each puzzle type
const DEFAULT_CONFIGS = {
  [PUZZLE_TYPES.MEMORY_GAME]: {
    gridSize: 4,
    totalPairs: 8,
    timeAllowedSeconds: 120,
  },
  [PUZZLE_TYPES.PATTERN_RECOGNITION]: {
    totalRounds: 5,
    startingDifficulty: 1,
    maxDifficulty: 5,
    timePerRoundSeconds: 30,
  },
};

/**
 * PuzzleOverlay - Manager component that renders the appropriate puzzle based on type
 *
 * @param {Object} props
 * @param {string} props.puzzleType - Type of puzzle (memory_game, pattern_recognition)
 * @param {string} props.puzzleId - Unique identifier for the puzzle
 * @param {Object} props.config - Configuration for the puzzle
 * @param {Function} props.onStart - Called when puzzle starts
 * @param {Function} props.onComplete - Called when puzzle is completed with results
 * @param {Function} props.onSkip - Called when user skips the puzzle
 */
const PuzzleOverlay = ({
  puzzleType,
  puzzleId,
  config,
  onStart,
  onComplete,
  onSkip,
}) => {
  // Notify that puzzle started when component mounts
  useEffect(() => {
    if (onStart && puzzleId) {
      onStart(puzzleId);
    }
  }, [puzzleId, onStart]);

  // Merge provided config with defaults
  const mergedConfig = {
    ...DEFAULT_CONFIGS[puzzleType],
    ...config,
  };

  // Handle puzzle completion
  const handleComplete = (result) => {
    if (onComplete) {
      onComplete({
        ...result,
        puzzleType,
      });
    }
  };

  // Handle puzzle skip
  const handleSkip = (id) => {
    if (onSkip) {
      onSkip(id);
    }
  };

  // Render the appropriate puzzle component
  switch (puzzleType) {
    case PUZZLE_TYPES.MEMORY_GAME:
      return (
        <MemoryGame
          config={mergedConfig}
          puzzleId={puzzleId}
          onComplete={handleComplete}
          onSkip={handleSkip}
        />
      );

    case PUZZLE_TYPES.PATTERN_RECOGNITION:
      return (
        <PatternRecognition
          config={mergedConfig}
          puzzleId={puzzleId}
          onComplete={handleComplete}
          onSkip={handleSkip}
        />
      );

    default:
      console.warn(`Unknown puzzle type: ${puzzleType}`);
      return null;
  }
};

export default PuzzleOverlay;
